import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Clock, Zap, AlertCircle, Play, Pause, PowerOff } from 'lucide-react';
import { QueueStatus } from '@/types/queue-monitoring';

interface QueueStatusCardProps {
  queue: QueueStatus;
}

const STATUS_CONFIG = {
  running: {
    label: 'Rodando',
    color: 'bg-green-500',
    icon: <Play className="h-4 w-4 text-white" />,
  },
  paused_limit: {
    label: 'Pausada (Limite)',
    color: 'bg-yellow-500',
    icon: <Pause className="h-4 w-4 text-white" />,
  },
  paused_schedule: {
    label: 'Pausada (Horário)',
    color: 'bg-blue-500',
    icon: <Clock className="h-4 w-4 text-white" />,
  },
  inactive: {
    label: 'Inativa',
    color: 'bg-gray-500',
    icon: <PowerOff className="h-4 w-4 text-white" />,
  },
};

const QueueStatusCard: React.FC<QueueStatusCardProps> = ({ queue }) => {
  const config = STATUS_CONFIG[queue.status];
  const progressValue = queue.dailyLimit ? (queue.dailyCount / queue.dailyLimit) * 100 : 0;

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="text-lg font-semibold truncate" title={queue.clientName}>
            {queue.clientName}
          </span>
          <Badge className={`flex items-center gap-2 ${config.color}`}>
            {config.icon}
            {config.label}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-grow flex flex-col justify-between">
        <div>
          <div className="text-sm text-gray-500 mb-2 flex items-center">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span>{queue.reason}</span>
          </div>

          {queue.dailyLimit !== null && (
            <div className="my-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Limite Diário</span>
                <span className="text-sm font-bold">{`${queue.dailyCount} / ${queue.dailyLimit}`}</span>
              </div>
              <Progress value={progressValue} />
            </div>
          )}
        </div>

        <div className="text-xs text-gray-400 mt-4 space-y-2">
          {queue.schedule && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{queue.schedule}</span>
            </div>
          )}
          {queue.rateLimit && (
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              <span>{queue.rateLimit}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QueueStatusCard; 